import { Dispatch, PayloadAction, createSlice } from "@reduxjs/toolkit";
import { ErrorDetailContainer } from "../api/config";
import {
    CreateActivityInput,
    FilterActivitiesInput,
    SaleActivityDto,
    SaleActivityStatus,
    SaleActivityType,
    SaleDto,
    SaleService,
    SaleStatus,
    UserDto,
} from "../api/psc";
import { getCurrentDateStr } from "../utils/date";
import {
    addOrUpdateSaleAction,
    addOrUpdateSaleActivityAction,
    deleteActivityAction,
} from "./SharedActions";

export interface ActivityState {
    isFetching: boolean;
    activities: SaleActivityDto[];
    users: UserDto[];
    totalCount: number;
    limit: 18;
    page: number;
    filter: FilterActivitiesInput;
    reminderNotificationBadgeCount: number;
    taskNotificationBadgeCount: number;
}

const initialState: ActivityState = {
    isFetching: true,
    activities: [],
    users: [],
    totalCount: 0,
    limit: 18,
    page: 0,
    filter: {
        offset: 0,
        statuses: [SaleActivityStatus.PENDING],
        type: null,
        userIds: [],
        createDateFrom: null,
        createDateTo: null,
        dueDateFrom: null,
        dueDateTo: getCurrentDateStr(),
    },
    reminderNotificationBadgeCount: 0,
    taskNotificationBadgeCount: 0,
};

const activitySlice = createSlice({
    name: "activities",
    initialState,
    reducers: {
        setActivitiesInitialState(
            state: ActivityState,
            action: PayloadAction<{
                currentUser: UserDto;
                users: UserDto[];
                reminderNotificationBadgeCount: number;
                taskNotificationBadgeCount: number;
            }>
        ) {
            return {
                ...initialState,
                users: action.payload.users,
                filter: {
                    ...state.filter,
                    status: [SaleStatus.PENDING],
                    userIds: [action.payload.currentUser.id],
                },
                reminderNotificationBadgeCount:
                    action.payload.reminderNotificationBadgeCount,
                taskNotificationBadgeCount:
                    action.payload.taskNotificationBadgeCount,
            };
        },
        setActivities(
            state: ActivityState,
            action: PayloadAction<{
                activities: SaleActivityDto[];
                totalCount: number;
            }>
        ) {
            return {
                ...state,
                activities: action.payload.activities,
                totalCount: action.payload.totalCount,
                isFetching: false,
            };
        },
        clearActivities(
            state: ActivityState,
            action: PayloadAction<SaleActivityType>
        ) {
            return {
                ...state,
                activities: [],
                isFetching: true,
                filter: { ...state.filter, type: action.payload },
            };
        },
        setActivitiesPage(state: ActivityState, action: PayloadAction<number>) {
            return {
                ...state,
                page: action.payload,
                filter: {
                    ...state.filter,
                    offset: action.payload * state.limit,
                },
            };
        },
        setActivitiesFilter(
            state: ActivityState,
            action: PayloadAction<FilterActivitiesInput>
        ) {
            return {
                ...state,
                filter: action.payload,
            };
        },
        setBadgeNotificationCount(
            state: ActivityState,
            action: PayloadAction<{
                reminderNotificationBadgeCount: number;
                taskNotificationBadgeCount: number;
            }>
        ) {
            return {
                ...state,
                reminderNotificationBadgeCount:
                    action.payload.reminderNotificationBadgeCount,
                taskNotificationBadgeCount:
                    action.payload.taskNotificationBadgeCount,
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(
            addOrUpdateSaleAction,
            (state: ActivityState, action: PayloadAction<SaleDto>) => {
                const updatedActivities = [...state.activities];
                for (const activity of action.payload.activities) {
                    const activityToBeUpdated = updatedActivities.find(
                        (a) => a.id === activity.id
                    );
                    if (activityToBeUpdated) {
                        const index =
                            updatedActivities.indexOf(activityToBeUpdated);
                        updatedActivities[index] = activity;
                    }
                }
                return { ...state, activities: updatedActivities };
            }
        );
        builder.addCase(
            addOrUpdateSaleActivityAction,
            (state: ActivityState, action: PayloadAction<SaleActivityDto>) => {
                const updatedActivities = [...state.activities];
                const activityToBeUpdated = updatedActivities.find(
                    (a) => a.id === action.payload.id
                );
                if (activityToBeUpdated) {
                    const index =
                        updatedActivities.indexOf(activityToBeUpdated);
                    updatedActivities[index] = action.payload;
                }
                return { ...state, activities: updatedActivities };
            }
        );
        builder.addCase(
            deleteActivityAction,
            (state: ActivityState, action: PayloadAction<number>) => {
                const updatedActivities = [
                    ...state.activities.filter((a) => a.id !== action.payload),
                ];
                return { ...state, activities: updatedActivities };
            }
        );
    },
});

export function resetAndFetchActivities(
    type: SaleActivityType,
    state: ActivityState,
    dispatch: Dispatch
) {
    dispatch(clearActivities(type));
    dispatch(setActivitiesPage(0));
    SaleService.listActivities("1", {
        ...state.filter,
        offset: 0,
        type: type,
    }).then((r) => {
        dispatch(setActivities({ activities: r.results, totalCount: r.count }));
    });
}

export function fetchActivities(
    type: SaleActivityType,
    state: ActivityState,
    dispatch: Dispatch,
    page: number
) {
    dispatch(clearActivities(type));
    dispatch(setActivitiesPage(page));
    SaleService.listActivities("1", {
        ...state.filter,
        offset: page * state.limit,
        type: type,
    }).then((r) => {
        dispatch(setActivities({ activities: r.results, totalCount: r.count }));
    });
}

export async function updateActivity(
    activityId: number,
    activity: CreateActivityInput,
    state: ActivityState,
    dispatch: Dispatch
): Promise<{
    error?: ErrorDetailContainer;
}> {
    try {
        const r = await SaleService.updateActivity(activityId, "1", activity);

        dispatch(addOrUpdateSaleActivityAction(r));

        const badgeCount = await SaleService.getBadgeCount("1");

        dispatch(
            setBadgeNotificationCount({
                reminderNotificationBadgeCount: badgeCount.reminderCount,
                taskNotificationBadgeCount: badgeCount.taskCount,
            })
        );

        return {};
    } catch (e) {
        return { error: e as ErrorDetailContainer };
    }
}

export async function addActivity(
    saleId: number,
    activity: CreateActivityInput,
    state: ActivityState,
    dispatch: Dispatch
): Promise<{
    error?: ErrorDetailContainer;
}> {
    try {
        const r = await SaleService.addActivity(saleId, "1", activity);

        dispatch(addOrUpdateSaleActivityAction(r));

        const badgeCount = await SaleService.getBadgeCount("1");

        dispatch(
            setBadgeNotificationCount({
                reminderNotificationBadgeCount: badgeCount.reminderCount,
                taskNotificationBadgeCount: badgeCount.taskCount,
            })
        );

        return {};
    } catch (e) {
        return { error: e as ErrorDetailContainer };
    }
}

export async function deleteActivity(
    activityId: number,
    state: ActivityState,
    dispatch: Dispatch
): Promise<{
    error?: ErrorDetailContainer;
}> {
    try {
        await SaleService.deleteActivity(activityId, "1");

        dispatch(deleteActivityAction(activityId));

        const badgeCount = await SaleService.getBadgeCount("1");

        dispatch(
            setBadgeNotificationCount({
                reminderNotificationBadgeCount: badgeCount.reminderCount,
                taskNotificationBadgeCount: badgeCount.taskCount,
            })
        );

        return {};
    } catch (e) {
        return { error: e as ErrorDetailContainer };
    }
}

export async function fetchActivityBadgeData(dispatch: Dispatch) {
    const badgeCount = await SaleService.getBadgeCount("1");

    dispatch(
        setBadgeNotificationCount({
            reminderNotificationBadgeCount: badgeCount.reminderCount,
            taskNotificationBadgeCount: badgeCount.taskCount,
        })
    );
}

export const {
    setActivitiesInitialState,
    setActivities,
    clearActivities,
    setActivitiesPage,
    setActivitiesFilter,
    setBadgeNotificationCount,
} = activitySlice.actions;
export default activitySlice.reducer;
